import { FileExcelOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, message, Space } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WebGrid from '../../webgrid/src';
import {
    formatLei,
    getPhysicalInventoryRowColor,
    type PhysicalInventoryItem,
} from '../domain/goodsSale';
import useFileDownload from '../hooks/useFileDownload';
import useRequest from '../hooks/useRequest';

interface PhysicalInventoryResponse {
    success: boolean;
    data: { items: PhysicalInventoryItem[] };
}

interface PhysicalInventoryPanelProps {
    /** CSS height of the grid; defaults to filling the viewport below the toolbar. */
    height?: string;
}

/**
 * The "Inventar fizic" stock-take grid (legacy `tsInvFizic`): lists the goods in
 * stock, lets the operator tick the physical check and accounting confirmation
 * and add notes, and exports the listing to Excel. Shared by the Goods Sale page
 * and the Control Room "Controlling" tab.
 */
const PhysicalInventoryPanel = ({
    height = 'calc(100vh - 260px)',
}: PhysicalInventoryPanelProps) => {
    const { t } = useTranslation();
    const request = useRequest();
    const downloadExcel = useFileDownload();
    const [items, setItems] = useState<PhysicalInventoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const result = await request<PhysicalInventoryResponse>({
                path: '/goods-sale/physical-inventory',
            });
            setItems(result.data.items);
        } catch (error) {
            message.error(t('goodsSale.loadError'));
            console.error('Failed to load physical inventory', error);
        } finally {
            setLoading(false);
        }
    }, [request, t]);

    useEffect(() => {
        void loadItems();
    }, [loadItems]);

    const updateItem = useCallback(
        async (row: PhysicalInventoryItem, updates: Partial<PhysicalInventoryItem>) => {
            setItems(previous =>
                previous.map(item => (item.id === row.id ? { ...item, ...updates } : item)),
            );
            try {
                await request({
                    path: `/goods-sale/physical-inventory/${row.id}`,
                    method: 'PATCH',
                    params: updates,
                });
            } catch (error) {
                message.error(t('goodsSale.physicalInventoryUpdateError'));
                console.error('Failed to update physical inventory row', error);
                void loadItems();
            }
        },
        [request, t, loadItems],
    );

    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            await downloadExcel('/goods-sale/physical-inventory/export', 'inventar_fizic.xlsx');
            message.success(t('common.exportSuccess'));
        } catch (error) {
            message.error(t('goodsSale.physicalInventoryExportError'));
            console.error('Failed to export physical inventory', error);
        } finally {
            setExporting(false);
        }
    }, [downloadExcel, t]);

    const columns = useMemo(
        () => [
            {
                key: 'nrContract',
                title: t('goodsSale.contractNumber'),
                dataIndex: 'nrContract',
                sortable: true,
                filterable: true,
            },
            {
                key: 'denumire',
                title: t('goodsSale.description'),
                dataIndex: 'denumire',
                sortable: true,
                filterable: true,
            },
            {
                key: 'um',
                title: t('goodsSale.unit'),
                dataIndex: 'um',
            },
            {
                key: 'cantitate',
                title: t('goodsSale.quantity'),
                dataIndex: 'cantitate',
            },
            {
                key: 'sumainLei',
                title: t('goodsSale.paid'),
                dataIndex: 'sumainLei',
                render: (value: unknown) => formatLei(value as number),
            },
            {
                key: 'invFizic',
                title: t('goodsSale.physicallyFound'),
                dataIndex: 'invFizic',
                render: (value: unknown, row: PhysicalInventoryItem) => (
                    <Checkbox
                        checked={Boolean(value)}
                        onChange={event => updateItem(row, { invFizic: event.target.checked })}
                    />
                ),
            },
            {
                key: 'contabilitate',
                title: t('goodsSale.accountingConfirmed'),
                dataIndex: 'contabilitate',
                render: (value: unknown, row: PhysicalInventoryItem) => (
                    <Checkbox
                        checked={Boolean(value)}
                        onChange={event => updateItem(row, { contabilitate: event.target.checked })}
                    />
                ),
            },
            {
                key: 'mentiuni',
                title: t('goodsSale.notes'),
                dataIndex: 'mentiuni',
                render: (value: unknown, row: PhysicalInventoryItem) => (
                    <Input
                        defaultValue={(value as string | null) ?? ''}
                        onBlur={event => {
                            if (event.target.value !== (row.mentiuni ?? '')) {
                                updateItem(row, { mentiuni: event.target.value });
                            }
                        }}
                    />
                ),
            },
        ],
        [t, updateItem],
    );

    return (
        <div>
            <Space style={{ marginBottom: 12 }} wrap>
                <Button icon={<ReloadOutlined />} onClick={loadItems}>
                    {t('goodsSale.refresh')}
                </Button>
                <Button icon={<FileExcelOutlined />} loading={exporting} onClick={handleExport}>
                    {t('goodsSale.physicalInventoryPrint')}
                </Button>
                <span>{t('goodsSale.itemCount', { count: items.length })}</span>
            </Space>
            <WebGrid<PhysicalInventoryItem>
                data={items}
                columns={columns}
                loading={loading}
                pagination
                pageSize={20}
                filterBar
                getRowStyle={row => ({ backgroundColor: getPhysicalInventoryRowColor(row) })}
                rowIdAccessor={row => row.id}
                height={height}
                emptyMessage={t('goodsSale.noData')}
            />
        </div>
    );
};

export default PhysicalInventoryPanel;
